// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CombinationChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import ChartHasThresholdOptionBuilder from './Izenda.ChartHasThresholdOptionBuilder';
import VISUAL_TYPE from 'share/components/charting/highcharts/Izenda.VisualTypeMapping';

import highchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import jsFormatService from 'share/Izenda.JsFormatService';
import { get, getOr, escape, assign, merge, isNil, filter, findIndex, find, assignIn, flow, forEach, orderBy, isBoolean } from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';

const Y_AXIS_CONTAINER_NAME = 'values';
@exportClass('CombinationChartOptionsBuilder')
export default class CombinationChartOptionsBuilder extends ChartHasThresholdOptionBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   * @returns {Object}
   */
  buildOptionsByType(visualType: string, userOptions: any, dataParser: any): any {
    let chartOptions = super.buildOptionsByType(visualType, userOptions, dataParser, false) || {};

    const yAxisContainerName = getOr(Y_AXIS_CONTAINER_NAME, 'Y_AXIS_CONTAINER_NAME', userOptions);

    const dataStructure = dataParser.dataStructure || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const defaultColors = this.DefaultColors;

    userOptions.chart.type = '';

    const combinationSeries = chartOptions.series;
    const thresholdSeries = chartOptions.thresholdSeries;
    const { izSingleYAxis } = userOptions;
    const isMultiSeries = combinationSeries.length > 1;
    const doesTransformToSingleYAxis = izSingleYAxis && isMultiSeries && !this.chartOptions.isSetting;

    chartOptions.series = [...combinationSeries, ...thresholdSeries];

    /**
     * Get Y-axis field that threshold belongs to
     * @param {Object} thresholdSerie
     * @returns {Object}
     */
    const getYAxisFieldOfThresholdSeries = (thresholdSerie: any) => {
      const fieldOptions = this.fieldOptions;
      const thresholdFieldNameAlias = getOr('', 'yAxisField.fieldNameAlias', thresholdSerie);

      const yAxisField = find(combinationSerie => {
        const fieldNameAlias = combinationSerie.fieldNameAlias;
        const dynamicThresholds = fieldOptions[fieldNameAlias].dynamicThresholds;

        return find({ fieldName: thresholdFieldNameAlias }, dynamicThresholds);
      }, dataStructure[yAxisContainerName]);

      return yAxisField;
    };

    setConfigSerie(userOptions, chartOptions, doesTransformToSingleYAxis);

    return chartOptions;

    /**
     * Set config for series
     * @param {Object} userOptions
     * @param {Object} chartOptions
     * @param {boolean} doesTransformToSingleYAxis
     */
    function setConfigSerie(userOptions: any, chartOptions: any, doesTransformToSingleYAxis: boolean): void {
      const orgYaxis = chartOptions.yAxis;
      const colors = isNil(userOptions.colors) ? defaultColors : userOptions.colors;
      chartOptions.yAxis = [];

      const lineSeries = filter(s => s.type === VISUAL_TYPE.Line || s.type === VISUAL_TYPE.Area, chartOptions.series);
      if (lineSeries.length > 0) {
        // Line and Area do not support colorByPoint.
        // So we need to get the colorByPoint which will be a flag to manually set color for point
        const colorByPoint = get('plotOptions.series.colorByPoint', userOptions);
        const colorOptions = {
          izMultiColor: colorByPoint,
          izDisableMultiColor: userOptions.izDisableMultiColor
        };
        highchartUtils.setColorPointForAreaLine(colorOptions, lineSeries, colors);

        const plotOptions = {
          series: {
            colorByPoint: false
          }
        };

        chartOptions.plotOptions = merge(chartOptions.plotOptions, plotOptions);
      }
      if (doesTransformToSingleYAxis) {
        const color = colors[0];
        const allYAxisOptions = assignIn(orgYaxis, {
          title: {
            text: localizer.getText('COMMON_LABEL_ALL'),
            style: {
              color: color
            }
          },
          labels: {
            style: {
              color: color
            }
          }
        });
        chartOptions.yAxis.push(allYAxisOptions);
      } else {
        const yAxisMapping = [];

        let yAxisLeftCount = 0;
        let yAxisRightCount = 0;

        // Count left/right Y-axises which are specified
        forEach(serieConfig => {
          const opposite = getOr(null, 'axisOption.opposite', serieConfig);
          if(opposite === true){
            yAxisRightCount++;
          }else if(opposite === false){
            yAxisLeftCount++;
          }
        }, izendaSeriesConfig);

        // Set index for each serie
        let serieIndex = 0;
        forEach(serie => {
          serie.serieIndex = serieIndex;
          serieIndex++;
        }, chartOptions.series);

        const mainSeries = flow(filter(serie => !serie.isThreshold), orderBy(['yAxisName'], ['asc']))(chartOptions.series);
        forEach(serie => {
          let yAxisIndex = chartOptions.yAxis.length;
          let yAxisName = serie.yAxisName;
          let serieIndex = serie.serieIndex;
          let serieColor = colors[serieIndex];
          let serieName = serie.name;
          let serieMappingName = serieName;
          let addYAxis = true;

          if(!isMultiSeries && yAxisName){
            serieMappingName = serieName;
            serieName = yAxisName;
          } else if(isMultiSeries && yAxisName){
            const mappingItem = find(item => item.serieName === yAxisName, yAxisMapping);
            yAxisIndex = mappingItem.index;
            addYAxis = false;
          }

          serie.yAxis = yAxisIndex;
          yAxisMapping.push({
            serieName: serieMappingName,
            index: yAxisIndex
          });

          if(!addYAxis)
          {
            return;
          }

          let yAxisOptions = assignIn(orgYaxis, {
            title: {
              text: serieName,
              style: {
                color: serieColor
              }
            },
            plotLines: get('[' + serieName + '].threshold', izendaSeriesConfig)
          });

          yAxisOptions = merge(yAxisOptions, {
            labels: {
              style: {
                color: serieColor
              }
            }
          });

          const axisOption = getOr({}, '[' + serieName + '].axisOption', izendaSeriesConfig);
          if(!isBoolean(axisOption.opposite)){
            if(yAxisLeftCount > yAxisRightCount){
              axisOption.opposite = true;
              yAxisRightCount++;
            }else{
              axisOption.opposite = false;
              yAxisLeftCount++;
            }
          }
          yAxisOptions = merge(yAxisOptions, axisOption);

          chartOptions.yAxis.push(yAxisOptions);

        }, mainSeries);

        const thresholdSeries = filter(serie => serie.isThreshold, chartOptions.series);
        forEach(serie => {
          const thresholdSerieField = getYAxisFieldOfThresholdSeries(serie);
          const mappingItem = find(item => item.serieName === thresholdSerieField.fieldNameAlias, yAxisMapping);
          serie.yAxis = mappingItem.index;
        }, thresholdSeries);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.CombinationChartOptionsBuilder.js